import { CategoryInfo } from "@/apis/workbook/types";
import type { TaskStatusValue } from "@/types/dsd";
import { useCategory } from "@/hooks/use-category";
import {
  useResolveChangedTables,
  useUnresolvedChangedTableIds,
} from "@/hooks/use-changed-note-tables";
import useClearQueryCache from "@/hooks/use-clear-query-cache";
import {
  useWorkbookId,
  useWorkbookIdAndCategoryId,
} from "@/hooks/use-workbook";
import { cn } from "@/lib/utils";
import { useMappingStore } from "@/stores/mapping";
import { Button } from "@/styles/ui/button";
import { useSidebar } from "@/styles/ui/sidebar";
import { FolderClosed, Loader2, TriangleAlert } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  getSheetIdToStatusMap,
  useDsdCategories,
  useDsdTaskStatus,
  useRetryAnalyzeXbrl,
} from "./hooks";
import { TaskStatusDisplay } from "./task-status-display";

/** 같은 카테고리에 대한 재시도 쿨다운(ms). polling 반복 시 API 중복 호출 방지. */
const RETRY_COOLDOWN_MS = 60 * 1000;

export const SidebarWorkspaceBodyFolder = ({ name }: { name: string }) => {
  return (
    <div className="flex gap-2 h-8 p-2 items-center">
      <FolderClosed className="w-4 h-4" />
      <span className="text-sm font-sans font-normal text-[#0A0A0A]">
        {name}
      </span>
    </div>
  );
};

export const SidebarWorkspaceBodyFolderItemsContainer = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <div className="flex flex-col py-0.5 px-1.5 gap-1 relative justify-center items-center">
      {children}
    </div>
  );
};

export const SidebarWorkspaceBodyFolderItem = ({
  category,
  isActive,
  taskStatus,
  hasChanges,
  onOpened,
  onRetryFailed,
}: {
  category: CategoryInfo;
  isActive: boolean;
  taskStatus?: TaskStatusValue;
  /** 버전 업데이트로 직전본과 달라진 표가 이 주석에 있음 */
  hasChanges?: boolean;
  /** 이 주석을 열었을 때 — 변경 알림을 내리는 데 쓴다 */
  onOpened?: () => void;
  onRetryFailed?: () => void;
}) => {
  const { setOpen } = useSidebar();
  const navigate = useNavigate();
  const { workbookId } = useWorkbookIdAndCategoryId();

  const clearSearch = useMappingStore((state) => state.search.clear);
  const clearDetail = useMappingStore((state) => state.detail.clear);

  const clearMappingSearchResults = () => {
    clearSearch();
    clearDetail();
  };

  const clearQueryCache = useClearQueryCache();

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (isActive) return;
      e.preventDefault();

      clearQueryCache();
      setOpen(false);
      clearMappingSearchResults();
      onOpened?.();

      // RUNNING 낙관적 표시는 useRetryAnalyzeXbrl 의 onMutate 가 소유한다. 여기서 미리
      // 써넣으면 쿨다운에 막혀 재시도가 안 걸린 경우까지 진행 중으로 보인다.
      if (taskStatus === "FAILED") {
        onRetryFailed?.();
      }

      navigate(`/mapping/${workbookId}/${category.id}`);
    },
    [
      isActive,
      setOpen,
      category,
      workbookId,
      navigate,
      clearMappingSearchResults,
      onOpened,
      onRetryFailed,
    ],
  );

  // NOT_TARGET 은 COMPLETED 와 같이 "더 진행될 것이 없는" 상태다. 진행 상태로 치면
  // 빈 아이콘이 변경 알림 배지 자리를 차지해 배지가 사라진다.
  const showTaskStatus =
    taskStatus !== undefined &&
    taskStatus !== "COMPLETED" &&
    taskStatus !== "NOT_TARGET";

  return (
    <Button
      key={category.category}
      variant="ghost"
      className={cn(
        "flex justify-between w-full h-7 px-2 items-center gap-2 rounded-md ",
        "hover:bg-neutral-100 active:bg-neutral-100",
        isActive &&
          "bg-white shadow-[0px_0px_0px_2px_#0000001A] border border-[#D2D2D2]",
      )}
      onClick={handleClick}
    >
      <div className="label-1-normal-regular text-ellipsis overflow-hidden whitespace-nowrap flex-1 min-w-0 text-left">
        {category.category}
      </div>
      {/* 분석이 끝나기 전에는 진행 상태만 보여준다 — 아직 도는 중인 주석에 변경 알림까지
          함께 띄우면 아이콘이 둘로 겹쳐 무엇을 보라는 안내인지 흐려진다. */}
      {showTaskStatus ? (
        <TaskStatusDisplay status={taskStatus} />
      ) : (
        hasChanges && (
          <TriangleAlert
            role="img"
            className="w-4 h-4 shrink-0 text-status-caution"
            aria-label="변경 사항 있음"
          />
        )
      )}
    </Button>
  );
};

export const SidebarWorkspaceBody = () => {
  const { category: activeCategory } = useCategory();
  const { open: sidebarOpen } = useSidebar();
  const { data: workbookId } = useWorkbookId();
  const { categoryId } = useWorkbookIdAndCategoryId();
  const navigate = useNavigate();
  const {
    data: categories,
    isLoading,
    isError,
    refetch: refetchCategories,
  } = useDsdCategories(workbookId!);

  const { data: taskStatusData } = useDsdTaskStatus(workbookId ?? undefined, {
    enablePolling: sidebarOpen,
  });
  useEffect(() => {
    if (!taskStatusData) return;
    refetchCategories();
  }, [taskStatusData, refetchCategories]);

  const { mutate: retryAnalyzeXbrl } = useRetryAnalyzeXbrl();
  const lastRetriedCategoryIdRef = useRef<string | null>(null);
  const lastRetriedAtRef = useRef<number>(0);

  const sheetIdToStatus = useMemo(
    () => (taskStatusData ? getSheetIdToStatusMap(taskStatusData) : {}),
    [taskStatusData],
  );

  // 버전 업데이트로 바뀐 표를 가진 주석에 경고 아이콘을 붙인다.
  const changedTableIds = useUnresolvedChangedTableIds(workbookId);
  const { mutate: resolveChanges } = useResolveChangedTables(workbookId);
  const changedTableIdsByCategory = useMemo(() => {
    const byCategory = new Map<string, string[]>();
    if (changedTableIds.size === 0 || !categories) return byCategory;
    for (const category of categories) {
      const changed: string[] = [];
      for (const table of category.tables) {
        if (changedTableIds.has(table.id)) changed.push(table.id);
      }
      if (changed.length > 0) byCategory.set(category.id, changed);
    }
    return byCategory;
  }, [changedTableIds, categories]);

  const createRetryHandler = useCallback(
    (catId: string) => () => {
      if (!taskStatusData) return;
      if (sheetIdToStatus[catId] !== "FAILED") return;

      const now = Date.now();
      const sameCategory = lastRetriedCategoryIdRef.current === catId;
      const withinCooldown = now - lastRetriedAtRef.current < RETRY_COOLDOWN_MS;
      if (sameCategory && withinCooldown) return;

      lastRetriedCategoryIdRef.current = catId;
      lastRetriedAtRef.current = now;
      retryAnalyzeXbrl([catId]);
    },
    [taskStatusData, retryAnalyzeXbrl],
  );

  useEffect(() => {
    if (!workbookId || !activeCategory || !taskStatusData) return;
    if (sheetIdToStatus[activeCategory.id] !== "FAILED") return;

    const now = Date.now();
    const sameCategory = lastRetriedCategoryIdRef.current === activeCategory.id;
    const withinCooldown = now - lastRetriedAtRef.current < RETRY_COOLDOWN_MS;
    if (sameCategory && withinCooldown) return;

    lastRetriedCategoryIdRef.current = activeCategory.id;
    lastRetriedAtRef.current = now;
    retryAnalyzeXbrl([activeCategory.id]);
  }, [workbookId, activeCategory?.id, taskStatusData, retryAnalyzeXbrl]);

  // categoryId가 URL에 없을 때 첫 번째 카테고리로 navigate
  useEffect(() => {
    if (!categoryId && categories && categories.length > 0 && workbookId) {
      navigate(`/mapping/${workbookId}/${categories[0].id}`, { replace: true });
    }
  }, [categoryId, categories, workbookId, navigate]);

  if (isLoading || isError || !categories)
    return (
      <div className="flex-1 h-full flex items-center justify-center">
        <Loader2 className="animate-spin" />
      </div>
    );
  if (isError) return <div>Error</div>;

  return (
    <div className="flex flex-col overflow-y-auto p-2">
      <SidebarWorkspaceBodyFolder name="주석" />
      <SidebarWorkspaceBodyFolderItemsContainer>
        {categories.map((category) => (
          <SidebarWorkspaceBodyFolderItem
            key={category.category}
            category={category}
            isActive={categoryId === category.id}
            taskStatus={sheetIdToStatus[category.id]}
            hasChanges={changedTableIdsByCategory.has(category.id)}
            onOpened={() => {
              const changed = changedTableIdsByCategory.get(category.id);
              if (changed) resolveChanges(changed);
            }}
            onRetryFailed={createRetryHandler(category.id)}
          />
        ))}
      </SidebarWorkspaceBodyFolderItemsContainer>
    </div>
  );
};
